import React from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import ResponsiveImage from '../ResponsiveImage'
import Placeholder from '../Placeholder'
import { vw } from '../../style/vw'
import isEmpty from 'lodash/isEmpty'
import theme from '../../style/theme'
import { LinkWithCursorHoverEffect } from '../Link'

const IMAGE_ASPECT = 618 / 450

const Card = (props) => {
  const { className, image, title, subtitle, description, link, placeholder, borderColor } = props

  // TEST: Check if LinkWithCursorHoverEffect component is causing the issue
  let classes
  try {
    classes = useStyles({ borderColor })
    console.log('useStyles with LinkWithCursorHoverEffect component successful')
  } catch (error) {
    console.error('Error in useStyles with LinkWithCursorHoverEffect:', error)
    return <div>Error in styles with LinkWithCursorHoverEffect: {error.message}</div>
  }

  console.log('Card props:', { title, subtitle, description, placeholder })

  // SAFE handling of description for prep time
  const prepTime = (() => {
    try {
      if (!description || typeof description !== 'string') {
        return 'Over 1 hour'
      }
      const parts = description.split(':')
      if (parts.length > 1) {
        const lastPart = parts[parts.length - 1]
        if (lastPart && typeof lastPart === 'string') {
          const trimmed = lastPart.trim()
          return trimmed || 'Over 1 hour'
        }
      }
      return 'Over 1 hour'
    } catch (error) {
      console.warn('Error parsing prep time from description:', description, error)
      return 'Over 1 hour'
    }
  })()

  if (placeholder) {
    return (
      <article className={cn(classes.card, className)} data-prep-time={prepTime}>
        <Placeholder className={classes.image} type='image' aspect={IMAGE_ASPECT} />
        <div className={classes.body}>
          <Placeholder className={classes.title} width='80%' />
          <Placeholder className={classes.subtitle} width='60%' />
          <Placeholder className={classes.description} width='80%' />
          <Placeholder className={classes.linkPlaceholder} width='40%' />
        </div>
      </article>
    )
  }

  // Safe prop handling
  const safeTitle = title || 'No Title'
  const safeSubtitle = subtitle || ''
  const safeDescription = description || ''
  const safeLink = link && typeof link === 'object' ? link : null
  const safeImage = image && typeof image === 'object' ? image : null

  return (
    <article className={cn(classes.card, className)} data-meal-prep-time={prepTime}>
      {safeLink?.url ? (
        <a href={safeLink.url} className={classes.imageLink}>
          {safeImage ? (
            <ResponsiveImage {...safeImage} aspect={IMAGE_ASPECT} />
          ) : (
            <div className={classes.imagePlaceholder}>No Image</div>
          )}
        </a>
      ) : (
        <div className={classes.imageLink}>
          {safeImage ? (
            <ResponsiveImage {...safeImage} aspect={IMAGE_ASPECT} />
          ) : (
            <div className={classes.imagePlaceholder}>No Image</div>
          )}
        </div>
      )}

      <div className={classes.body}>
        {safeSubtitle && <p className={classes.subtitle}>{safeSubtitle}</p>}
        <h3 className={classes.title}>{safeTitle}</h3>
        {safeDescription && <p className={classes.description}>{safeDescription}</p>}

        {/* TEST: Using LinkWithCursorHoverEffect component */}
        {!safeLink && <div className={classes.linkPlaceholder} />}
        {safeLink && !isEmpty(safeLink.url) && (
          <LinkWithCursorHoverEffect link={safeLink} className={classes.link}>
            Read more
          </LinkWithCursorHoverEffect>
        )}
      </div>
    </article>
  )
}

const useStyles = createUseStyles({
  card: {
    border: 'none',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
    maxHeight: '549px',
    minHeight: '549px',
    position: 'relative',
    '@media (max-width: 980px)': {
      // maxHeight: '470px',
      minHeight: '400px'
    },
    '@media (max-width: 600px)': {
      // maxHeight: '470px',
      minHeight: '400px'
    }
  },
  imageLink: {
    display: 'block',
    transition: 'transform 0.2s cubic-bezier(.4,0,.2,1)',
    '&:hover': {
      transform: 'scale(1.03)'
    }
  },
  image: {
    width: '100%',
    height: 'auto'
  },
  imagePlaceholder: {
    width: '100%',
    height: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    color: '#666',
    fontSize: vw(14)
  },
  body: {
    padding: '10px 0px',
    display: 'flex',
    flexDirection: 'column',
    gap: vw(1)
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#38524F',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
      paddingTop: '10px',
      paddingBottom: '10px'
    },
    paddingTop: '10px',
    paddingBottom: '10px',
    lineHeight: '1.3em'
  },
  subtitle: {
    fontSize: '14px',
    color: '#38524F',
    fontWeight: '400',
    margin: 0,
    textTransform: 'capitalize',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    }
  },
  description: {
    fontSize: '16px',
    color: '#38524F',
    fontWeight: '400',
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px'
    }
  },
  link: {
    width: 'fit-content',
    fontSize: '16px',
    color: '#38524F',
    fontWeight: '500',
    fontFamily: theme.fonts.unquotedBodyFace,
    textTransform: 'uppercase',
    textDecoration: 'none',
    borderBottom: '1px solid #38524F',
    marginTop: '40px',
    '&:hover': {
      color: '#38524F'
    },
    position: 'absolute',
    bottom: '5px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
      marginTop: '15px',
      position: 'relative'
    }
  },
  linkPlaceholder: {
    height: 20
  }
}, { name: 'Card' })

export default Card
